import { useSimpleError } from 'hooks';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useToast } from './toasts/useToast';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';
import saveAs from 'file-saver';
import { convertBase64ToFile } from 'utils/base64FileConversion';
import { useQueryWithSimpleErrorHandling } from 'hooks';

const SINGLE_UPLOAD_MUTATION = gql`
  mutation singleUpload($file: Upload, $metadata: SingleUploadInput!) {
    singleUpload(file: $file, metadata: $metadata) {
      fileUniqueId
      uploadDate
    }
  }
`;

const GET_FILE_QUERY = gql`
  query dmsFile($fileId: ID!) {
    dmsFile(fileId: $fileId) {
      fileName
      content
    }
  }
`;

const FILE_VALIDATION_RULES = gql`
  query fileValidationRules {
    fileValidationRules {
      maxSize
      allowedExtensions
    }
  }
`;

const getFileExtension = fileName => {
  return fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length) || fileName;
};

export function useFileUpload({ onStarting = _ => {}, onCompleted = _ => {}, onError = _ => {}, showNotifications = true } = {}) {
  const addToast = useToast();
  const { t } = useTranslation();
  const showError = useSimpleError();

  const [uploadFileMutation, { loading: uploading, data, error, ...rest }] = useMutation(SINGLE_UPLOAD_MUTATION);

  const { data: fileValidationRules } = useQueryWithSimpleErrorHandling(FILE_VALIDATION_RULES, {});

  const validateFileExtension = useCallback(
    (file, returnParams) => {
      const extension = getFileExtension(file.name).toLowerCase();
      const allowedExtensions = fileValidationRules.fileValidationRules.allowedExtensions;
      if (allowedExtensions.find(x => x.toLowerCase() === extension)) return true;

      const message = t('FileUpload.UnsupportedExtension', {
        extension,
        allowedExtensions: allowedExtensions.toString().toLowerCase()
      });
      onError({ message, networkError: { message } }, returnParams);
      return false;
    },
    [fileValidationRules, onError, t]
  );

  const validateFileSize = useCallback(
    (file, returnParams) => {
      const maxSize = fileValidationRules.fileValidationRules.maxSize;
      if (!maxSize) return true;
      if (file.size <= maxSize * 1024 * 1024) return true;
      const message = t('FileUpload.MaxLimitReached', { size: fileValidationRules.fileValidationRules.maxSize });
      onError({ message, networkError: { message } }, returnParams);
      return false;
    },
    [fileValidationRules, onError, t]
  );

  const fileId = data && data.singleUpload.fileUniqueId;

  const uploadFile = useCallback(
    (file, metadata, returnParams) => {
      if (!file) {
        return;
      }

      if (!validateFileExtension(file, returnParams) || !validateFileSize(file, returnParams)) {
        return;
      }

      onStarting(file);

      uploadFileMutation({
        variables: { file, metadata },
        onCompleted: result => {
          onCompleted({
            fileUniqueId: result.singleUpload.fileUniqueId,
            uploadedDate: result.singleUpload.uploadDate,
            ...returnParams
          });
          showNotifications && addToast(t('File.Uploaded'), 'success', { toastId: 'FileUploadedMessageToast' });
        },
        onError: err => {
          onError(err, returnParams);
          showNotifications && showError(err);
        }
      });
    },
    [
      validateFileExtension,
      validateFileSize,
      onStarting,
      uploadFileMutation,
      onCompleted,
      showNotifications,
      addToast,
      t,
      onError,
      showError
    ]
  );

  return [uploadFile, { uploading, fileId, maxSize: fileValidationRules?.fileValidationRules?.maxSize, error, ...rest }];
}

export function useFileDownload({
  customQuery = GET_FILE_QUERY,
  customOperation = 'dmsFile',
  onStarting = () => {},
  onCompleted = () => {},
  onError = () => {},
  showNotifications = true,
  showDownloadedFile = true
} = {}) {
  const showError = useSimpleError();
  const [fileId, setFileId] = useState(undefined);

  const [getFileQuery, { loading: downloading, data, error, ...rest }] = useLazyQuery(customQuery, {
    onCompleted: result => {
      const file = result[customOperation];
      onCompleted(file);
      showDownloadedFile && saveAs(convertBase64ToFile(file.content), file.fileName);
    },
    onError: err => {
      onError(err);
      showNotifications && showError(err);
    },
    notifyOnNetworkStatusChange: true
  });

  const fileName = data && data[customOperation] && data[customOperation].fileName;

  const downloadFile = useCallback(
    fileId => {
      if (!fileId) {
        return;
      }
      setFileId(fileId);
      onStarting(fileId);
      getFileQuery({ variables: { fileId } });
    },
    [getFileQuery, onStarting, setFileId]
  );

  return [downloadFile, { downloading, fileId, fileName, error, ...rest }];
}
